import { ONBOARDING_FINAL_STEP, type OnboardingState } from '@doneverse/shared'

/**
 * Whether to throw the founder a party for finishing onboarding — step 11,
 * "Congratulations", per the Founder Onboarding brief.
 *
 * Deliberately NOT the same rule as the unlock moment (step 5), which fires on
 * the false → true edge of `unlocked` inside one mount and is never persisted.
 * That works for the unlock because the founder is there by construction: step 5
 * is the direct consequence of them finishing the product tour. Step 11 is not —
 * it closes when step 10 does, and step 10 closes on a launch date or an admin
 * event that can land at 3am. An edge-only celebration would be seen by whoever
 * happened to have the tab open, which for the one screen the brief asks to be
 * celebratory is close to nobody.
 *
 * So this asks a question the server can answer on any later page load — "is
 * this founder freshly finished?" — and keeps only the one bit the server does
 * not know: whether this browser has already thrown the party. That bit is
 * keyed by RUN, so an admin reset (which opens run N+1) re-arms the celebration
 * rather than suppressing it forever.
 */

/**
 * How long after the final step closes the celebration is still worth showing.
 *
 * Long enough to survive a founder who finishes on a Friday and next opens the
 * dashboard on a Monday; short enough that nobody who completed onboarding last
 * quarter gets confetti because they cleared their browser storage.
 */
export const ONBOARDING_CELEBRATION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

const CELEBRATED_KEY = 'doneverse.onboarding.celebrated'
const PANEL_DISMISSED_KEY = 'doneverse.onboarding.panelDismissed'

/** Per user, because an admin impersonating two founders shares one browser. */
function readRun(key: string, userId: string): number | null {
  if (!userId || typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(`${key}.${userId}`)
    if (!raw) return null
    const run = Number(raw)
    return Number.isInteger(run) ? run : null
  } catch {
    // Private mode, disabled storage, quota. Failing to READ means the founder
    // may see the celebration twice, which is the harmless direction.
    return null
  }
}

function writeRun(key: string, userId: string, runNumber: number): void {
  if (!userId || typeof window === 'undefined') return

  try {
    window.localStorage.setItem(`${key}.${userId}`, String(runNumber))
  } catch {
    // Ignore storage errors (quota/private mode/etc)
  }
}

/** The onboarding run this browser has already celebrated, if any. */
export function readCelebratedRun(userId: string): number | null {
  return readRun(CELEBRATED_KEY, userId)
}

export function markRunCelebrated(userId: string, runNumber: number): void {
  writeRun(CELEBRATED_KEY, userId, runNumber)
}

/**
 * The run whose finished panel this browser has been told to put away.
 *
 * A second bit, not a reuse of the celebration's: the modal is dismissed the
 * moment it opens (a founder who closes the tab mid-confetti has had their
 * moment), while the panel behind it stays on the page until the founder
 * actually chooses to put it away. Sharing one flag would take the panel down
 * with the confetti and leave a finished founder with no journey to look back
 * at — or, keyed the other way, replay the party on every load.
 *
 * Keyed by run for the same reason: an admin reset opens run N+1, and the new
 * journey deserves its panel back.
 */
export function readPanelDismissedRun(userId: string): number | null {
  return readRun(PANEL_DISMISSED_KEY, userId)
}

export function markRunPanelDismissed(userId: string, runNumber: number): void {
  writeRun(PANEL_DISMISSED_KEY, userId, runNumber)
}

/**
 * True when the final step closed recently and this browser has not yet
 * celebrated the run it closed.
 *
 * `now` is injected rather than read from the clock so the window is testable
 * and so a caller cannot accidentally evaluate this against two different
 * "nows" within one decision.
 */
export function shouldCelebrateOnboardingFinish(
  state: OnboardingState | null | undefined,
  celebratedRun: number | null,
  now: number,
): boolean {
  if (!state) return false
  if (celebratedRun === state.runNumber) return false

  const final = state.steps.find((step) => step.stepNumber === ONBOARDING_FINAL_STEP)
  if (!final || final.status !== 'complete') return false

  // No timestamp means we cannot tell fresh from ancient. Backfilled and
  // repaired rows are exactly the ones that turn up without one, and they are
  // the ones a congratulations screen would be strangest for.
  if (!final.completedAt) return false
  const completedAt = Date.parse(final.completedAt)
  if (Number.isNaN(completedAt)) return false

  // Age only, with no lower bound: a client clock running behind the server
  // dates the completion in the future, and the founder watching their own step
  // close is the one case that must never be dropped.
  return now - completedAt <= ONBOARDING_CELEBRATION_WINDOW_MS
}
